import { Controller } from "@hotwired/stimulus";

// Drives the "Import survey from PDF" modal.
// Connects to data-controller="pdf-import"
//
// States it manages on the same modal markup:
//   - empty     (initial, default state)
//   - hover     (file is being dragged across the dropzone)
//   - selected  (file picked, valid, ready to import)
//   - error     (file rejected client-side: size, mime, etc.)
//
// Server-side validation is the source of truth; the client checks
// duplicate the server contract so we can give immediate feedback
// instead of a round-trip per bad file.
export default class extends Controller<HTMLElement> {
  static targets = [
    "input",
    "dropzone",
    "submit",
    "empty",
    "selected",
    "error",
    "fileName",
    "fileSize",
    "errorMessage",
  ];

  static values = {
    maxBytes: { type: Number, default: 10 * 1024 * 1024 },
    acceptedTypes: { type: Array, default: ["application/pdf", "application/x-pdf"] },
    errorTooLarge: { type: String, default: "That PDF is too large." },
    errorWrongType: { type: String, default: "That file isn't a PDF." },
  };

  declare inputTarget: HTMLInputElement;
  declare dropzoneTarget: HTMLElement;
  declare submitTarget: HTMLButtonElement;
  declare hasSubmitTarget: boolean;
  declare emptyTarget: HTMLElement;
  declare selectedTarget: HTMLElement;
  declare errorTarget: HTMLElement;
  declare fileNameTarget: HTMLElement;
  declare fileSizeTarget: HTMLElement;
  declare errorMessageTarget: HTMLElement;
  declare maxBytesValue: number;
  declare acceptedTypesValue: string[];
  declare errorTooLargeValue: string;
  declare errorWrongTypeValue: string;

  connect() {
    this.renderEmpty();
  }

  // <input type="file"> change handler
  fileChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) {
      this.renderEmpty();
      return;
    }
    this.consumeFile(file);
  }

  // Drag handlers on the dropzone
  dragEnter(event: DragEvent) {
    event.preventDefault();
    this.dropzoneTarget.classList.add("is-dragover");
  }

  dragOver(event: DragEvent) {
    event.preventDefault();
    this.dropzoneTarget.classList.add("is-dragover");
  }

  dragLeave(event: DragEvent) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("is-dragover");
  }

  drop(event: DragEvent) {
    event.preventDefault();
    this.dropzoneTarget.classList.remove("is-dragover");

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    // Assign the dropped file to the hidden input so the form submits it.
    const dt = new DataTransfer();
    dt.items.add(file);
    this.inputTarget.files = dt.files;
    this.consumeFile(file);
  }

  removeFile(event: Event) {
    event.preventDefault();
    this.inputTarget.value = "";
    this.renderEmpty();
  }

  // -- internal --

  consumeFile(file: File) {
    const validation = this.validate(file);
    if (validation) {
      this.renderError(validation);
      return;
    }
    this.renderSelected(file);
  }

  validate(file: File): string | null {
    if (!this.acceptedTypesValue.includes(file.type)) {
      return this.errorWrongTypeValue;
    }
    if (file.size > this.maxBytesValue) {
      return this.errorTooLargeValue.replace("%{limit}", this.formatBytes(this.maxBytesValue));
    }
    return null;
  }

  renderEmpty() {
    this.emptyTarget.classList.remove("hidden");
    this.selectedTarget.classList.add("hidden");
    this.errorTarget.classList.add("hidden");
    this.setSubmitDisabled(true);
  }

  renderSelected(file: File) {
    this.emptyTarget.classList.add("hidden");
    this.selectedTarget.classList.remove("hidden");
    this.errorTarget.classList.add("hidden");
    this.fileNameTarget.textContent = file.name;
    this.fileSizeTarget.textContent = this.formatBytes(file.size);
    this.setSubmitDisabled(false);
  }

  renderError(message: string) {
    this.emptyTarget.classList.remove("hidden");
    this.selectedTarget.classList.add("hidden");
    this.errorTarget.classList.remove("hidden");
    this.errorMessageTarget.textContent = message;
    this.setSubmitDisabled(true);
  }

  setSubmitDisabled(disabled: boolean) {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.disabled = disabled;
    if (disabled) {
      this.submitTarget.classList.add("btn-disabled");
    } else {
      this.submitTarget.classList.remove("btn-disabled");
    }
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
